// /admin/audit — v5 Audit Log surface.
//
// Replaces the single-day MVP at pages/admin/Audit.tsx. Drives the new BFF
// surface (LLD §8): /api/admin/audit/events, /facets, /events/{id}.
//
// Defensive rendering: until the BFF agent finishes Phase 3 the new
// endpoints return UnknownOperationException (Coral router). The audit
// client surfaces those as `null` and we fall back to the legacy
// `/api/admin/audit?date=` endpoint, adapting its row shape to the v5
// fields. Once the new endpoints respond, the fallback becomes dead code.
//
// Filter state lives in URL search params so a user can share a filtered
// view: ?from=…&to=…&location_id=a,b&event_type=…&actor_alias=…&q=…&page=…
// &page_size=…. Multi-valued axes are comma-joined in the URL but emitted
// as repeated params on the wire.
//
// Search input is debounced (300ms) before mutating the URL `q` param, so
// every keystroke isn't a fetch.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { Button, StatusChip } from "@/components";
import { auditClient } from "@/data/clients/auditClient";
import type { AuditSubscriptionFilterSpec } from "@/data/clients/auditClient";
import { useRole } from "@/role";
import { useSession } from "@/session";
import { ExportModal } from "./ExportModal";
import { SubscribeModal } from "./SubscribeModal";
import "../AdminShared.css";
import "./Audit.css";

import {
  ActiveChips,
  type ActiveChip,
} from "./ActiveChips";
import {
  DateRangePicker,
  presetToRange,
  type DateRange,
} from "./DateRangePicker";
import { FacetPopover, type FacetOption } from "./FacetPopover";
import { AuditTable } from "./AuditTable";
import {
  AUDIT_COLUMNS,
  loadVisibility,
  saveVisibility,
  type AuditColumnKey,
} from "./columns";
import { cleanLabel } from "./labels";
import type {
  AuditEvent,
  AuditFacetsResponse,
  AuditListResponse,
  RangePreset,
} from "./types";

// ---------------------------------------------------------------------------
// URL ↔ filter-state codec
// ---------------------------------------------------------------------------

function todayIsoUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Title-case a server-side category enum (e.g. "ROLE" → "Role changes"). */
function humanizeCategory(cat: string): string {
  switch (cat) {
    case "ROLE":      return "Role changes";
    case "CAMERA":    return "Camera changes";
    case "VIDEO":     return "Video & live view";
    case "MODE":      return "Mode changes";
    case "LOCATION":  return "Location changes";
    case "ACCESS":    return "Sessions & access";
    case "INSTALLER": return "Installer & devices";
    case "IDENTITY":  return "Identity";
    case "SESSION":   return "Sessions & access";
    default:          return cat.charAt(0) + cat.slice(1).toLowerCase();
  }
}

function readMulti(params: URLSearchParams, key: string): string[] {
  const raw = params.getAll(key);
  // Accept both repeated and comma-joined forms for resilience to manual
  // URL edits.
  const expanded: string[] = [];
  for (const r of raw) {
    for (const piece of r.split(",")) {
      const trimmed = piece.trim();
      if (trimmed.length > 0) expanded.push(trimmed);
    }
  }
  return Array.from(new Set(expanded));
}

interface ParsedFilters {
  from: string;
  to: string;
  preset: RangePreset;
  locationIds: string[];
  eventTypes: string[];
  actorAliases: string[];
  q: string;
  page: number;
  pageSize: 25 | 50 | 100;
}

function parseFilters(params: URLSearchParams): ParsedFilters {
  const today = todayIsoUtc();
  const presetRaw = params.get("preset") ?? "";
  let from = params.get("from") ?? "";
  let to = params.get("to") ?? "";
  let preset: RangePreset = "today";

  if (presetRaw === "today" || presetRaw === "yesterday" || presetRaw === "7d" || presetRaw === "30d") {
    preset = presetRaw;
    const r = presetToRange(preset, { from: today, to: today });
    from = r.from;
    to = r.to;
  } else if (from && to) {
    preset = "custom";
  } else {
    // Default — last 7 days. Matches the v5 mockup's "has-value" selection
    // and gives the operator a sensible baseline that won't return 0 rows.
    preset = "7d";
    const r = presetToRange("7d", { from: today, to: today });
    from = r.from;
    to = r.to;
  }

  const pageSizeRaw = parseInt(params.get("page_size") ?? "50", 10);
  const pageSize: 25 | 50 | 100 =
    pageSizeRaw === 25 || pageSizeRaw === 50 || pageSizeRaw === 100
      ? pageSizeRaw
      : 50;

  return {
    from,
    to,
    preset,
    locationIds: readMulti(params, "location_id"),
    eventTypes: readMulti(params, "event_type"),
    actorAliases: readMulti(params, "actor_alias"),
    q: params.get("q") ?? "",
    page: Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1),
    pageSize,
  };
}

function writeFilters(
  current: URLSearchParams,
  patch: Partial<ParsedFilters>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  // Build the merge from the *already-parsed* current state to ensure we
  // don't inherit stale repeated keys.
  const merged: ParsedFilters = { ...parseFilters(current), ...patch };

  next.delete("preset");
  next.delete("from");
  next.delete("to");
  next.delete("location_id");
  next.delete("event_type");
  next.delete("actor_alias");
  next.delete("q");
  next.delete("page");
  next.delete("page_size");

  if (merged.preset === "custom") {
    next.set("from", merged.from);
    next.set("to", merged.to);
  } else {
    next.set("preset", merged.preset);
  }
  if (merged.locationIds.length > 0)
    next.set("location_id", merged.locationIds.join(","));
  if (merged.eventTypes.length > 0)
    next.set("event_type", merged.eventTypes.join(","));
  if (merged.actorAliases.length > 0)
    next.set("actor_alias", merged.actorAliases.join(","));
  if (merged.q.length > 0) next.set("q", merged.q);
  if (merged.page > 1) next.set("page", String(merged.page));
  if (merged.pageSize !== 50) next.set("page_size", String(merged.pageSize));

  return next;
}

// ---------------------------------------------------------------------------
// Pager — "1 2 3 … N" with a Prev/Next-only mode when truncated
// ---------------------------------------------------------------------------

interface PagerProps {
  page: number;
  totalPages: number;
  truncated: boolean;
  pageSize: 25 | 50 | 100;
  total: number;
  rowsOnPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: 25 | 50 | 100) => void;
}

function Pager(props: PagerProps): JSX.Element {
  const { page, totalPages, truncated, pageSize, total, rowsOnPage, onPageChange, onPageSizeChange } = props;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = total === 0 ? 0 : start + rowsOnPage - 1;

  const pageButtons: React.ReactNode[] = [];
  if (!truncated) {
    const window: number[] = [];
    const N = totalPages;
    for (let i = 1; i <= N; i++) {
      if (i === 1 || i === N || (i >= page - 1 && i <= page + 1)) {
        window.push(i);
      }
    }
    let last = 0;
    for (const i of window) {
      if (i - last > 1) {
        pageButtons.push(
          <span key={`gap-${i}`} className="rb-audit__pager-ellipsis">
            …
          </span>,
        );
      }
      pageButtons.push(
        <button
          key={i}
          type="button"
          className={
            i === page ? "rb-audit__pager-active" : undefined
          }
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>,
      );
      last = i;
    }
  }

  return (
    <div className="rb-audit__pager">
      <span className="rb-audit__pager-info">
        Showing <b>{start}</b>–<b>{end}</b> of <b>{total.toLocaleString()}</b>{" "}
        matching event{total === 1 ? "" : "s"}
        {truncated && (
          <span style={{ marginLeft: 8, color: "#866e04" }}>
            (truncated — use Prev/Next)
          </span>
        )}
      </span>
      <div className="rb-audit__pager-nav">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          ← Prev
        </button>
        {pageButtons}
        <button
          type="button"
          disabled={page >= totalPages || rowsOnPage === 0}
          onClick={() => onPageChange(page + 1)}
        >
          Next →
        </button>
        <select
          className="rb-audit__pager-size"
          value={pageSize}
          onChange={(e) =>
            onPageSizeChange(parseInt(e.target.value, 10) as 25 | 50 | 100)
          }
          aria-label="Page size"
        >
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function AdminAuditPage(): JSX.Element {
  const handleApiError = useApiErrorHandler();
  const { scopedFetchToken } = useRole();
  const { session } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Export counts surface a badge on the persistent "Exports" header button
  // so the operator knows their queued exports are still in flight — and that
  // a finished export is waiting — without navigating away. `inFlight` is the
  // queued/running count (drives the 4s poll); `ready` is the downloadable
  // count.
  const [exportCounts, setExportCounts] = useState<{
    ready: number;
    inFlight: number;
  }>({ ready: 0, inFlight: 0 });

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<25 | 50 | 100>(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingLegacy, setUsingLegacy] = useState(false);

  const [facets, setFacets] = useState<AuditFacetsResponse | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [openPopover, setOpenPopover] = useState<
    "locations" | "actions" | "actors" | null
  >(null);

  // Column visibility — persisted in localStorage (LS_VISIBILITY in AuditTable).
  const [columnVisible, setColumnVisible] = useState<Record<AuditColumnKey, boolean>>(
    loadVisibility,
  );
  const [colsPopoverOpen, setColsPopoverOpen] = useState(false);
  const colsAnchorRef = useRef<HTMLDivElement>(null);

  // Click-outside for the columns popover.
  useEffect(() => {
    if (!colsPopoverOpen) return;
    function handler(ev: MouseEvent) {
      if (!colsAnchorRef.current) return;
      if (colsAnchorRef.current.contains(ev.target as Node)) return;
      setColsPopoverOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colsPopoverOpen]);

  function toggleColumn(key: AuditColumnKey) {
    setColumnVisible((cur) => {
      const next = { ...cur, [key]: !cur[key] };
      saveVisibility(next);
      return next;
    });
  }

  // Debounced search input — the URL ?q is only updated 300ms after typing
  // stops. The search box is uncontrolled-from-URL on mount; subsequent URL
  // changes (e.g. from a chip removal) DO sync into the input.
  const [searchInput, setSearchInput] = useState(filters.q);
  useEffect(() => {
    setSearchInput(filters.q);
  }, [filters.q]);
  const searchTimer = useRef<number | null>(null);
  const onSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchTimer.current !== null) {
        window.clearTimeout(searchTimer.current);
      }
      searchTimer.current = window.setTimeout(() => {
        setSearchParams(
          (cur) => writeFilters(cur, { q: value, page: 1 }),
          { replace: false },
        );
      }, 300);
    },
    [setSearchParams],
  );

  // ---------- list fetch ----------
  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedId(null);
    try {
      const resp = await auditClient.listEvents({
        from: filters.from,
        to: filters.to,
        locationIds: filters.locationIds,
        eventTypes: filters.eventTypes,
        actorAliases: filters.actorAliases,
        q: filters.q || undefined,
        sort: "time.desc",
        page: filters.page,
        pageSize: filters.pageSize,
      });
      if (resp !== null) {
        setUsingLegacy(false);
        setEvents(resp.events);
        setPage(resp.page);
        setPageSize(resp.page_size as 25 | 50 | 100);
        setTotal(resp.total);
        setTotalPages(resp.total_pages || 1);
        setTruncated(resp.truncated === true);
      } else {
        // BFF endpoint not yet deployed — fall back to legacy single-day.
        setUsingLegacy(true);
        const legacyDate = filters.to; // Most recent day in the range.
        const legacy: AuditListResponse =
          await auditClient.legacyListByDate(legacyDate);
        setEvents(legacy.events);
        setPage(1);
        setPageSize(filters.pageSize);
        setTotal(legacy.total);
        setTotalPages(1);
        setTruncated(false);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code ?? String(err.status));
        handleApiError(err, { actionLabel: "Load audit events" });
      } else {
        setError("unknown");
      }
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    filters.from,
    filters.to,
    filters.locationIds,
    filters.eventTypes,
    filters.actorAliases,
    filters.q,
    filters.page,
    filters.pageSize,
    handleApiError,
  ]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents, scopedFetchToken]);

  // ---------- facet fetch ----------
  // Re-fetch facets every time the filter-set changes; the BFF returns
  // counts already adjusted for the *other* filters (LLD §8.2). We simply
  // pass through the same params.
  const loadFacets = useCallback(async () => {
    try {
      const f = await auditClient.getFacets({
        from: filters.from,
        to: filters.to,
        locationIds: filters.locationIds,
        eventTypes: filters.eventTypes,
        actorAliases: filters.actorAliases,
        q: filters.q || undefined,
      });
      setFacets(f);
    } catch {
      // Facets are non-critical — silent failure means the popovers fall
      // back to "no counts available".
      setFacets(null);
    }
  }, [
    filters.from,
    filters.to,
    filters.locationIds,
    filters.eventTypes,
    filters.actorAliases,
    filters.q,
  ]);

  useEffect(() => {
    void loadFacets();
  }, [loadFacets, scopedFetchToken]);

  // ---------- export counts (header "Exports" badge) ----------
  // Cheap poll: hit the list endpoint once on mount + once per scope change so
  // the badge is populated even when nothing is in flight, then every 4s while
  // at least one export is queued/running. Stops polling when no more
  // queued/running rows.
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    async function tick() {
      try {
        const resp = await auditClient.listExports();
        if (cancelled) return;
        const rows = resp.exports ?? [];
        const inFlight = rows.filter(
          (r) => r.status === "queued" || r.status === "running",
        ).length;
        const ready = rows.filter((r) => r.status === "ready").length;
        setExportCounts({ ready, inFlight });
        if (inFlight > 0) {
          timer = window.setTimeout(tick, 4000);
        }
      } catch {
        // Endpoint unreachable — leave counts at 0; the Export button still works.
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [scopedFetchToken, exportOpen]);

  // ---------- mutators ----------
  function setRange(preset: RangePreset, range: DateRange) {
    setSearchParams(
      (cur) =>
        writeFilters(cur, {
          preset,
          from: range.from,
          to: range.to,
          page: 1,
        }),
      { replace: false },
    );
  }

  function setMultiAxis(
    axis: "locationIds" | "eventTypes" | "actorAliases",
    values: string[],
  ) {
    setSearchParams(
      (cur) => writeFilters(cur, { [axis]: values, page: 1 }),
      { replace: false },
    );
  }

  function clearAll() {
    setSearchParams(
      (cur) =>
        writeFilters(cur, {
          locationIds: [],
          eventTypes: [],
          actorAliases: [],
          q: "",
          page: 1,
        }),
      { replace: false },
    );
  }

  function changePage(p: number) {
    setSearchParams(
      (cur) => writeFilters(cur, { page: p }),
      { replace: false },
    );
  }

  function changePageSize(s: 25 | 50 | 100) {
    setSearchParams(
      (cur) => writeFilters(cur, { pageSize: s, page: 1 }),
      { replace: false },
    );
  }

  // ---------- derived popover data ----------
  const locationOptions: FacetOption[] = useMemo(() => {
    if (facets?.locations) {
      return facets.locations.map((l) => ({
        value: l.id,
        label: l.name,
        mono: true,
        count: l.count,
      }));
    }
    // Best-effort: surface currently-selected ids so they're un-checkable
    // even when facets aren't populated yet.
    return filters.locationIds.map((id) => ({ value: id, label: id, mono: true }));
  }, [facets, filters.locationIds]);

  const actionOptions: FacetOption[] = useMemo(() => {
    if (facets?.event_types) {
      return facets.event_types.map((t) => ({
        value: t.type,
        label: cleanLabel(t.human || t.type),
        count: t.count,
        // PRD §1.11 — group event_types by category so the popover renders
        // a 2-level tree (Category → Action) and the parent toggle filters
        // all event_types in that category at once.
        group: humanizeCategory(t.category),
      }));
    }
    return filters.eventTypes.map((t) => ({ value: t, label: t }));
  }, [facets, filters.eventTypes]);

  const actorOptions: FacetOption[] = useMemo(() => {
    if (facets?.actors) {
      return facets.actors.map((a) => ({
        value: a.alias,
        label: `${a.displayName || a.alias} · ${a.alias}`,
        count: a.count,
      }));
    }
    return filters.actorAliases.map((a) => ({ value: a, label: a }));
  }, [facets, filters.actorAliases]);

  // ---------- active chips ----------
  function locationLabel(id: string): string {
    return (
      facets?.locations?.find((l) => l.id === id)?.name ?? id
    );
  }
  function eventTypeLabel(type: string): string {
    return (
      facets?.event_types?.find((t) => t.type === type)?.human ??
      type.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
    );
  }
  function actorLabel(alias: string): string {
    return (
      facets?.actors?.find((a) => a.alias === alias)?.displayName ?? alias
    );
  }

  const chips: ActiveChip[] = useMemo(() => {
    const out: ActiveChip[] = [];
    for (const id of filters.locationIds) {
      out.push({
        id: `loc-${id}`,
        label: locationLabel(id),
        onRemove: () =>
          setMultiAxis(
            "locationIds",
            filters.locationIds.filter((v) => v !== id),
          ),
      });
    }
    for (const t of filters.eventTypes) {
      out.push({
        id: `et-${t}`,
        label: eventTypeLabel(t),
        onRemove: () =>
          setMultiAxis(
            "eventTypes",
            filters.eventTypes.filter((v) => v !== t),
          ),
      });
    }
    for (const a of filters.actorAliases) {
      out.push({
        id: `act-${a}`,
        label: actorLabel(a),
        onRemove: () =>
          setMultiAxis(
            "actorAliases",
            filters.actorAliases.filter((v) => v !== a),
          ),
      });
    }
    if (filters.preset === "custom") {
      out.push({
        id: "date",
        label: `${filters.from} → ${filters.to}`,
        variant: "date",
        onRemove: () =>
          setRange("7d", presetToRange("7d", { from: filters.from, to: filters.to })),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, facets]);

  const hasActiveFilters =
    filters.locationIds.length > 0 ||
    filters.eventTypes.length > 0 ||
    filters.actorAliases.length > 0 ||
    filters.q.length > 0;

  // Filter spec to ship with a "Current filtered view" subscription. Empty
  // axes are omitted so the BFF doesn't have to filter on `[]`.
  const subscriptionFilterSpec: AuditSubscriptionFilterSpec = useMemo(() => {
    const spec: AuditSubscriptionFilterSpec = {
      from: filters.from,
      to: filters.to,
    };
    if (filters.locationIds.length > 0) spec.location_ids = filters.locationIds;
    if (filters.eventTypes.length > 0) spec.event_types = filters.eventTypes;
    if (filters.actorAliases.length > 0)
      spec.actor_aliases = filters.actorAliases;
    if (filters.q.length > 0) spec.q = filters.q;
    return spec;
  }, [
    filters.from,
    filters.to,
    filters.locationIds,
    filters.eventTypes,
    filters.actorAliases,
    filters.q,
  ]);

  const defaultEmail = session?.alias
    ? `${session.alias}@amazon.com`
    : "";

  // ---------- render ----------
  return (
    <section className="admin-page">
      <header className="rb-audit__header">
        <div className="rb-audit__title-row">
          <h1 className="page-title" style={{ margin: 0 }}>
            Audit Log
          </h1>
          <span className="rb-audit__count" aria-live="polite">
            {loading ? "…" : `${total.toLocaleString()} events`}
          </span>
          <span className="rb-audit__retention">
            Retained for 365 days · events appear within 5m · DSAR-compliant
          </span>
          <div className="rb-audit__actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSubscribeOpen(true)}
            >
              Subscribe
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setExportOpen(true)}
            >
              Export
            </Button>
            {/* Persistent ingress to the exports list — always visible so a
                finished export stays reachable. Badge counts ready + in-flight
                jobs; tooltip/aria break out the ready subset. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/audit/exports")}
              aria-label={
                exportCounts.ready + exportCounts.inFlight > 0
                  ? `Exports — ${
                      exportCounts.ready + exportCounts.inFlight
                    } export${
                      exportCounts.ready + exportCounts.inFlight === 1 ? "" : "s"
                    } (${exportCounts.ready} ready)`
                  : "Exports"
              }
              iconRight={
                exportCounts.ready + exportCounts.inFlight > 0 ? (
                  <span className="rb-audit__fbtn-count">
                    {exportCounts.ready + exportCounts.inFlight}
                  </span>
                ) : undefined
              }
            >
              Exports
            </Button>
          </div>
        </div>

        {usingLegacy && (
          <div className="rb-audit__legacy-banner" role="status">
            New audit endpoints are not yet deployed — showing single-day
            results from the legacy endpoint for {filters.to}. Filters and
            facet counts are disabled until the BFF rolls out.
          </div>
        )}

        <div className="rb-audit__filters">
          <DateRangePicker
            preset={filters.preset}
            range={{ from: filters.from, to: filters.to }}
            onChange={setRange}
          />

          <span className="rb-audit__divider" aria-hidden />

          <span className="rb-audit__flabel">Filters</span>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              className={
                filters.locationIds.length > 0
                  ? "rb-audit__fbtn rb-audit__fbtn--active"
                  : "rb-audit__fbtn"
              }
              onClick={() =>
                setOpenPopover((cur) => (cur === "locations" ? null : "locations"))
              }
              disabled={usingLegacy}
            >
              Locations
              {filters.locationIds.length > 0 && (
                <span className="rb-audit__fbtn-count">
                  {filters.locationIds.length}
                </span>
              )}
            </button>
            <FacetPopover
              open={openPopover === "locations"}
              searchPlaceholder={`Search ${facets?.locations?.length ?? 0} sites…`}
              options={locationOptions}
              selected={filters.locationIds}
              onApply={(next) => {
                setMultiAxis("locationIds", next);
                setOpenPopover(null);
              }}
              onClose={() => setOpenPopover(null)}
            />
          </div>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              className={
                filters.eventTypes.length > 0
                  ? "rb-audit__fbtn rb-audit__fbtn--active"
                  : "rb-audit__fbtn"
              }
              onClick={() =>
                setOpenPopover((cur) => (cur === "actions" ? null : "actions"))
              }
              disabled={usingLegacy}
            >
              Actions
              {filters.eventTypes.length > 0 && (
                <span className="rb-audit__fbtn-count">
                  {filters.eventTypes.length}
                </span>
              )}
            </button>
            <FacetPopover
              open={openPopover === "actions"}
              searchPlaceholder="Search actions…"
              options={actionOptions}
              selected={filters.eventTypes}
              onApply={(next) => {
                setMultiAxis("eventTypes", next);
                setOpenPopover(null);
              }}
              onClose={() => setOpenPopover(null)}
            />
          </div>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              className={
                filters.actorAliases.length > 0
                  ? "rb-audit__fbtn rb-audit__fbtn--active"
                  : "rb-audit__fbtn"
              }
              onClick={() =>
                setOpenPopover((cur) => (cur === "actors" ? null : "actors"))
              }
              disabled={usingLegacy}
            >
              Actors
              {filters.actorAliases.length > 0 && (
                <span className="rb-audit__fbtn-count">
                  {filters.actorAliases.length}
                </span>
              )}
            </button>
            <FacetPopover
              open={openPopover === "actors"}
              searchPlaceholder={`Search ${facets?.actors?.length ?? 0} users…`}
              options={actorOptions}
              selected={filters.actorAliases}
              onApply={(next) => {
                setMultiAxis("actorAliases", next);
                setOpenPopover(null);
              }}
              onClose={() => setOpenPopover(null)}
            />
          </div>

          <input
            type="search"
            className="rb-audit__search"
            placeholder="Search events (label, target, request id)…"
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={usingLegacy}
            aria-label="Search audit events"
          />

          <button
            type="button"
            className="rb-audit__clear-all"
            onClick={clearAll}
            disabled={!hasActiveFilters}
          >
            Clear all filters
          </button>

          <div ref={colsAnchorRef} style={{ position: "relative", marginLeft: "auto" }}>
            <button
              type="button"
              className="rb-audit__cols-btn"
              onClick={() => setColsPopoverOpen((o) => !o)}
              aria-expanded={colsPopoverOpen}
              aria-haspopup="menu"
            >
              Columns ▾
            </button>
            {colsPopoverOpen && (
              <div className="rb-audit__cols-popover" role="menu">
                {AUDIT_COLUMNS.filter((c) => c.hideable).map((c) => (
                  <label key={c.key} className="rb-audit__cols-row">
                    <input
                      type="checkbox"
                      checked={columnVisible[c.key] !== false}
                      onChange={() => toggleColumn(c.key)}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <ActiveChips chips={chips} />
      </header>

      {error !== null && (
        <div className="card" style={{ marginBottom: 12 }}>
          <StatusChip tone="danger">
            <code className="mono mono--sm">{error}</code>
          </StatusChip>
          <span className="admin-muted"> Failed to load audit events.</span>
        </div>
      )}

      <AuditTable
        events={events}
        loading={loading}
        expandedId={expandedId}
        onToggleExpand={(id) =>
          setExpandedId((cur) => (cur === id ? null : id))
        }
        emptyState="No matching events."
        visibleColumns={columnVisible}
        searchQuery={filters.q}
      />

      <Pager
        page={page}
        totalPages={totalPages}
        truncated={truncated}
        pageSize={pageSize}
        total={total}
        rowsOnPage={events.length}
        onPageChange={changePage}
        onPageSizeChange={changePageSize}
      />

      <SubscribeModal
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        onSaved={() => {
          /* No list to refresh here — the Subscriptions page reloads on
             its own mount/PATCH. */
        }}
        defaultEmail={defaultEmail}
        currentFilterSpec={subscriptionFilterSpec}
        mode="create"
      />

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onQueued={() => {
          // Bump the in-flight count optimistically so the Exports badge ticks
          // up before the next poll lands.
          setExportCounts((c) => ({ ...c, inFlight: c.inFlight + 1 }));
        }}
        currentFilterSpec={subscriptionFilterSpec}
      />
    </section>
  );
}
